import { useState, useRef, useEffect } from "react";
import { useMusic } from "../../../context/music";
import { useRouter } from "next/router";

export default function GlobalMusicPlayer() {
  const {
    activeTrack,
    isPlaying,
    progress,
    duration,
    isSeeking,
    audioRef,
    togglePlay,
    trackEnded,
    closePlayer,
    setDuration,
    setProgress,
    setIsSeeking,
    setTargetTab,
  } = useMusic();

  const router = useRouter();
  const [isClosing, setIsClosing] = useState(false);
  const progressBarRef = useRef(null);

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(null);

  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setProgress(audio.currentTime);
      }
    };

    const handleEnded = () => trackEnded();

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [isSeeking, activeTrack, trackEnded, setProgress]);

  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    setDragging(true);
    dragStartY.current = e.clientY;
    document.body.style.cursor = "grabbing";
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
  };

  const handleDragMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY });
    if (dragStartY.current !== null && e.clientY - dragStartY.current > 60) {
      setDragging(false);
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        closePlayer();
      }, 300);
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
    }
  };

  const handleDragEnd = () => {
    setDragging(false);
    document.body.style.cursor = "";
    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);
  };
  const handleDragEnter = () => setShowTooltip(true);
  const handleDragLeave = () => setShowTooltip(false);
  const handleDragOver = (e) => setTooltipPos({ x: e.clientX, y: e.clientY });

  useEffect(() => {
    if (!dragging) document.body.style.cursor = "";
  }, [dragging]);

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const getAudioDuration = () =>
    duration || (audioRef.current && audioRef.current.duration) || 0;

  const handleBarClick = (e) => {
    if (!progressBarRef.current || !activeTrack) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.type.startsWith("touch")
      ? e.touches[0].clientX - rect.left
      : e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const audioDuration = getAudioDuration();
    const newTime = percent * audioDuration;
    setProgress(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleThumbDown = (e) => {
    e.preventDefault();
    setIsSeeking(true);
    document.addEventListener("mousemove", handleThumbMove);
    document.addEventListener("mouseup", handleThumbUp);
    document.addEventListener("touchmove", handleThumbMove);
    document.addEventListener("touchend", handleThumbUp);
  };
  const handleThumbMove = (e) => {
    if (!progressBarRef.current || !activeTrack) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.type.startsWith("touch")
      ? e.touches[0].clientX - rect.left
      : e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const audioDuration = getAudioDuration();
    const newTime = percent * audioDuration;
    setProgress(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };
  const handleThumbUp = () => {
    setIsSeeking(false);
    document.removeEventListener("mousemove", handleThumbMove);
    document.removeEventListener("mouseup", handleThumbUp);
    document.removeEventListener("touchmove", handleThumbMove);
    document.removeEventListener("touchend", handleThumbUp);
  };

  const PlayIcon = (
    <svg
      width="27"
      height="26"
      viewBox="0 0 27 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.9">
        <path
          d="M13.2342 23.0449C19.3403 23.0449 24.2903 18.355 24.2903 12.5698C24.2903 6.78458 19.3403 2.09473 13.2342 2.09473C7.12817 2.09473 2.17822 6.78458 2.17822 12.5698C2.17822 18.355 7.12817 23.0449 13.2342 23.0449Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.66309 12.8115V11.0621C9.66309 8.88331 11.2883 7.99293 13.2784 9.08233L14.8815 9.96224L16.4846 10.8421C18.4747 11.9316 18.4747 13.7123 16.4846 14.8017L14.8815 15.6816L13.2784 16.5615C11.2883 17.6509 9.66309 16.7606 9.66309 14.5818V12.8115Z"
          stroke="white"
          strokeWidth="1.5"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
  const PauseIcon = (
    <svg
      width="23"
      height="21"
      viewBox="0 0 23 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.0298 16.8752V5.0252C10.0298 3.9002 9.493 3.4502 8.13684 3.4502H4.71818C3.36201 3.4502 2.8252 3.9002 2.8252 5.0252V16.8752C2.8252 18.0002 3.36201 18.4502 4.71818 18.4502H8.13684C9.493 18.4502 10.0298 18.0002 10.0298 16.8752Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.7774 16.8752V5.0252C19.7774 3.9002 19.2406 3.4502 17.8844 3.4502H14.4657C13.119 3.4502 12.5728 3.9002 12.5728 5.0252V16.8752C12.5728 18.0002 13.1096 18.4502 14.4657 18.4502H17.8844C19.2406 18.4502 19.7774 18.0002 19.7774 16.8752Z"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  const DownArrow = (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 13L16 21L24 13"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (!activeTrack) {
    return null;
  }

  return (
    <>
      <div
        className={`fixed w-full bottom-0 bg-[#2F3032] rounded-t-[26px] z-[999] left-0 flex flex-col items-center gap-2 transition-transform duration-300 ${
          isClosing ? "translate-y-full" : ""
        }  lg:items-center lg:gap-8 lg:pt-[10px] pb-[22px] lg:pl-[123px] lg:pr-[97px]`}
      >
        <div className="w-full relative flex flex-col items-center">
          <div className="w-12 h-1.5 bg-gray-400/60 hidden lg:block rounded-full mt-2 mb-1 mx-auto" />
          <div
            className="flex flex-col items-center pb-2 lg:hidden select-none cursor-pointer"
            onClick={() => {
              setIsClosing(true);
              setTimeout(() => {
                setIsClosing(false);
                closePlayer();
              }, 300);
            }}
          >
            {DownArrow}
            <span className="text-xs text-white opacity-70 mt-1">
              Tap to close
            </span>
          </div>
          <div
            className="hidden lg:block absolute top-0 left-0 w-full h-8 cursor-grab z-10"
            onMouseDown={handleDragStart}
            onMouseEnter={handleDragEnter}
            onMouseLeave={handleDragLeave}
            onMouseMove={handleDragOver}
            style={{ userSelect: "none" }}
          ></div>
          {showTooltip && (
            <div
              className="hidden lg:block fixed pointer-events-none z-[1000]"
              style={{
                left: tooltipPos.x + 16,
                top: tooltipPos.y - 24,
              }}
            >
              <div className="px-3 py-1 rounded bg-black text-white text-xs shadow-lg opacity-90">
                Drag down to close
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-between  items-center w-full px-4 pb-4 lg:items-center lg:px-0 lg:pb-0">
          <img
            src={activeTrack.image_url}
            alt={activeTrack.name}
            className="rounded-lg object-cover w-[55px] h-[55px] lg:w-[80px] lg:h-[80px] lg:mb-0 cursor-pointer"
            onClick={() => {
              if (activeTrack.artistPageUrl) {
                const [pathname] = activeTrack.artistPageUrl.split('?');
                setTargetTab("musicSetup");
                router.push(pathname);
              }
            }}
          />
          <div className="flex flex-col items-start w-full text-left lg:ml-6">
            <span className="truncate text-wrap text-white font-urbanist font-semibold text-[14px] leading-[18px] tracking-[0px] lg:text-[22px] lg:leading-[26px] lg:tracking-[0px]">
              {activeTrack.name}
            </span>
            <span className="truncate font-urbanist text-wrap font-semibold text-[12px] leading-[16px] tracking-[0px] text-[#979797] lg:text-[18px] lg:leading-[18px] lg:tracking-[0px]">
              {activeTrack.artists_name}
            </span>
          </div>
          <button
            className="w-14 h-14 flex items-center justify-center rounded-full lg:ml-6 lg:mt-0 lg:mb-0"
            onClick={togglePlay}
          >
            {isPlaying ? PauseIcon : PlayIcon}
          </button>
        </div>
        <div className="w-full px-6 lg:mt-0 lg:flex-1  lg:px-0">
          <div
            className="relative w-full h-[4px] bg-[#424242] rounded-full cursor-pointer"
            ref={progressBarRef}
            onClick={handleBarClick}
            onTouchStart={handleBarClick}
          >
            <div
              className="absolute top-0 left-0 h-[4px] bg-white rounded-full"
              style={{
                width: `${duration ? (progress / duration) * 100 : 0}%`,
              }}
            ></div>

            <div
              className="absolute top-[-2px] cursor-pointer"
              style={{
                left: `calc(${
                  duration ? (progress / duration) * 100 : 0
                }% - 2px )`,
              }}
              onMouseDown={handleThumbDown}
              onTouchStart={handleThumbDown}
            >
              <div className="w-[36px] h-2 bg-white rounded-full shadow" />
            </div>
          </div>
        </div>

        <audio
          ref={audioRef}
          src={activeTrack.preview_url}
          preload="auto"
          onEnded={trackEnded}
          onLoadedMetadata={handleLoadedMetadata}
        />
      </div>
    </>
  );
}
